import { useEffect, useState, useCallback } from "react";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, TrendingUp, Scale, Plus } from "lucide-react";

export default function Cash() {
  const { can, user } = useAuth();
  const { outletOptions, outletName } = useMeta();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";
  const [closings, setClosings] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closeOpen, setCloseOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cf, setCf] = useState({ date: new Date().toISOString().slice(0, 10), opening_cash: 0, cash_sales: 0, cash_expenses: 0, petty_cash: 0, deposits: 0, withdrawals: 0, actual_cash: 0, outlet_id: "" });
  const [ef, setEf] = useState({ date: new Date().toISOString().slice(0, 10), type: "cash_sale", amount: 0, notes: "", outlet_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([api.get("/cash/closings"), api.get("/cash/ledger")]);
      setClosings(c.data || []); setLedger(l.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const expected = Number(cf.opening_cash) + Number(cf.cash_sales) - Number(cf.cash_expenses) - Number(cf.petty_cash) - Number(cf.deposits) + Number(cf.withdrawals);
  const variance = Number(cf.actual_cash) - expected;

  const saveClosing = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/cash/closing", cf); toast.success("Daily closing saved"); setCloseOpen(false); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const saveEntry = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/cash/ledger", ef); toast.success("Cash entry added"); setEntryOpen(false); load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const latest = closings[0];
  const closingCols = [
    { key: "date", label: "Date", render: (r) => <span className="tabular">{r.date}</span> },
    ...(isSuper ? [{ key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) }] : []),
    { key: "opening_cash", label: "Opening", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.opening_cash) },
    { key: "cash_sales", label: "Cash Sales", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.cash_sales) },
    { key: "expected_cash", label: "Expected", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.expected_cash) },
    { key: "actual_cash", label: "Actual", className: "text-right", cellClassName: "tabular text-right", render: (r) => money(r.actual_cash) },
    { key: "variance", label: "Variance", className: "text-right", cellClassName: "tabular text-right", render: (r) => <span className={r.variance === 0 ? "" : r.variance > 0 ? "text-sky-600" : "text-red-600"}>{money(r.variance)}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];
  const ledgerCols = [
    { key: "date", label: "Date", render: (r) => <span className="tabular">{r.date}</span> },
    ...(isSuper ? [{ key: "outlet_id", label: "Outlet", render: (r) => outletName(r.outlet_id) }] : []),
    { key: "type", label: "Type", render: (r) => <span className="capitalize">{String(r.type).replace(/_/g, " ")}</span> },
    { key: "amount", label: "Amount", className: "text-right", cellClassName: "tabular text-right font-semibold", render: (r) => money(r.amount) },
    { key: "notes", label: "Notes" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Cash Management" subtitle="Daily closing, reconciliation and cash ledger per outlet.">
        {can("cash:create") && <>
          <Button variant="outline" onClick={() => setEntryOpen(true)} data-testid="add-cash-entry-btn"><Plus className="mr-1 h-4 w-4" />Cash Entry</Button>
          <Button onClick={() => setCloseOpen(true)} data-testid="add-closing-btn"><Scale className="mr-1 h-4 w-4" />Daily Closing</Button>
        </>}
      </PageHeader>

      {latest && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard label="Latest Expected" value={money(latest.expected_cash)} icon={TrendingUp} accent="bg-primary/10 text-primary" sub={latest.date} />
          <KpiCard label="Latest Actual" value={money(latest.actual_cash)} icon={Wallet} accent="bg-emerald-100 text-emerald-700" />
          <KpiCard label="Latest Variance" value={money(latest.variance)} icon={Scale} accent={latest.variance < 0 ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"} sub={latest.status} />
        </div>
      )}

      <Tabs defaultValue="closings">
        <TabsList><TabsTrigger value="closings" data-testid="tab-closings">Daily Closings</TabsTrigger><TabsTrigger value="ledger" data-testid="tab-ledger">Cash Ledger</TabsTrigger></TabsList>
        <TabsContent value="closings" className="mt-4"><DataTable columns={closingCols} rows={closings} loading={loading} total={closings.length} emptyTitle="No closings yet" emptyHint="Record a daily closing to reconcile cash." /></TabsContent>
        <TabsContent value="ledger" className="mt-4"><DataTable columns={ledgerCols} rows={ledger} loading={loading} total={ledger.length} emptyTitle="No cash entries" /></TabsContent>
      </Tabs>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="closing-form">
          <DialogHeader><DialogTitle className="font-display">Daily Cash Closing</DialogTitle></DialogHeader>
          <form onSubmit={saveClosing} className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="eyebrow">Date</Label><Input type="date" value={cf.date} onChange={(e) => setCf({ ...cf, date: e.target.value })} data-testid="cf-date" /></div>
            {isSuper && <div className="space-y-1.5"><Label className="eyebrow">Outlet</Label>
              <Select value={cf.outlet_id} onValueChange={(v) => setCf({ ...cf, outlet_id: v })}><SelectTrigger data-testid="cf-outlet"><SelectValue placeholder="Outlet" /></SelectTrigger><SelectContent>{outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>}
            {[["opening_cash", "Opening Cash"], ["cash_sales", "Cash Sales"], ["cash_expenses", "Cash Expenses"], ["petty_cash", "Petty Cash"], ["deposits", "Deposits"], ["withdrawals", "Withdrawals"], ["actual_cash", "Actual Counted"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label className="eyebrow">{l}</Label><Input type="number" step="0.01" value={cf[k]} onChange={(e) => setCf({ ...cf, [k]: e.target.value })} data-testid={`cf-${k}`} /></div>
            ))}
            <div className="col-span-2 flex items-center justify-between rounded-sm border border-border bg-secondary/50 p-3 text-sm">
              <span>Expected: <b className="tabular">{money(expected)}</b></span>
              <span className={variance === 0 ? "" : variance > 0 ? "text-sky-600" : "text-red-600"}>Variance: <b className="tabular">{money(variance)}</b></span>
            </div>
            <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="cf-submit">{busy ? "Saving…" : "Save closing"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent data-testid="entry-form">
          <DialogHeader><DialogTitle className="font-display">Cash Ledger Entry</DialogTitle></DialogHeader>
          <form onSubmit={saveEntry} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="eyebrow">Date</Label><Input type="date" value={ef.date} onChange={(e) => setEf({ ...ef, date: e.target.value })} data-testid="ef-date" /></div>
              <div className="space-y-1.5"><Label className="eyebrow">Type</Label>
                <Select value={ef.type} onValueChange={(v) => setEf({ ...ef, type: v })}><SelectTrigger data-testid="ef-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["cash_sale", "cash_expense", "petty_cash", "deposit", "withdrawal"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
              {isSuper && <div className="col-span-2 space-y-1.5"><Label className="eyebrow">Outlet</Label>
                <Select value={ef.outlet_id} onValueChange={(v) => setEf({ ...ef, outlet_id: v })}><SelectTrigger data-testid="ef-outlet"><SelectValue placeholder="Outlet" /></SelectTrigger><SelectContent>{outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>}
              <div className="space-y-1.5"><Label className="eyebrow">Amount</Label><Input type="number" step="0.01" value={ef.amount} onChange={(e) => setEf({ ...ef, amount: e.target.value })} data-testid="ef-amount" required /></div>
              <div className="space-y-1.5"><Label className="eyebrow">Notes</Label><Input value={ef.notes} onChange={(e) => setEf({ ...ef, notes: e.target.value })} data-testid="ef-notes" /></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEntryOpen(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="ef-submit">{busy ? "Saving…" : "Add entry"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
